import { LitElement, html, css, nothing } from "lit";
import type { CSSResultGroup, PropertyValues } from "lit";
import {
  DEFAULT_APP_BASE,
  IFRAME_LOAD_TIMEOUT_MS,
  WIDGET_VERSION,
  Z_INDEX_BASE,
  buildIframeUrl,
} from "./config.js";
import { currentSourceUrl } from "./source-path.js";
import type {
  InboundMessage,
  OutboundMessage,
  WidgetLocale,
  WidgetPosition,
  WidgetTheme,
} from "./types.js";

const STYLES = css`
  :host {
    all: initial;
    position: fixed;
    --_ksides-chat-offset: var(--ksides-chat-offset, 20px);
    --_ksides-chat-trigger-size: var(--ksides-chat-trigger-size, 56px);
    --_ksides-chat-panel-gap: var(--ksides-chat-panel-gap, 14px);
    --_ksides-chat-bottom-offset: var(
      --ksides-chat-bottom-offset,
      calc(var(--_ksides-chat-offset) + env(safe-area-inset-bottom))
    );
    --_ksides-chat-right-offset: var(
      --ksides-chat-right-offset,
      calc(var(--_ksides-chat-offset) + env(safe-area-inset-right))
    );
    --_ksides-chat-left-offset: var(
      --ksides-chat-left-offset,
      calc(var(--_ksides-chat-offset) + env(safe-area-inset-left))
    );
    bottom: var(--_ksides-chat-bottom-offset);
    right: var(--_ksides-chat-right-offset);
    z-index: var(--ksides-chat-z-index, ${Z_INDEX_BASE});
    display: block;
    width: var(--_ksides-chat-trigger-size);
    height: var(--_ksides-chat-trigger-size);
    font-family:
      system-ui,
      -apple-system,
      "Segoe UI",
      Roboto,
      "Apple SD Gothic Neo",
      "Noto Sans KR",
      sans-serif;
    color-scheme: light dark;
  }

  :host([position="bottom-left"]) {
    right: auto;
    left: var(--_ksides-chat-left-offset);
  }

  :host([hidden]) {
    display: none !important;
  }

  .trigger {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    background: var(--ksides-brand, #2563eb);
    color: #fff;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
    display: grid;
    place-items: center;
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
    padding: 0;
  }

  :host([position="bottom-left"]) .trigger {
    right: auto;
    left: 0;
  }

  .trigger:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
  }

  .trigger:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
    box-shadow:
      0 0 0 4px var(--ksides-brand, #2563eb),
      0 8px 24px rgba(0, 0, 0, 0.22);
  }

  .trigger svg {
    width: 26px;
    height: 26px;
    display: block;
  }

  .badge {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #ef4444;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    display: grid;
    place-items: center;
    box-shadow: 0 0 0 2px #fff;
  }

  .panel {
    position: fixed;
    bottom: calc(
      var(--_ksides-chat-bottom-offset) + var(--_ksides-chat-trigger-size) + var(--_ksides-chat-panel-gap)
    );
    right: var(--_ksides-chat-right-offset);
    width: min(var(--ksides-chat-panel-width, 400px), calc(100vw - 24px));
    height: min(
      var(--ksides-chat-panel-height, 640px),
      calc(
        100dvh - var(--_ksides-chat-bottom-offset) - var(--_ksides-chat-trigger-size) -
          var(--_ksides-chat-panel-gap) - 20px
      )
    );
    border-radius: 16px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
    transform-origin: bottom right;
    transform: translateY(8px) scale(0.98);
    opacity: 0;
    pointer-events: none;
    transition:
      transform 0.18s ease,
      opacity 0.18s ease;
  }

  :host([position="bottom-left"]) .panel {
    right: auto;
    left: var(--_ksides-chat-left-offset);
    transform-origin: bottom left;
  }

  .panel[data-open="true"] {
    transform: translateY(0) scale(1);
    opacity: 1;
    pointer-events: auto;
  }

  .panel iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
    background: transparent;
    color-scheme: light dark;
  }

  .loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: light-dark(#fff, #0b0f17);
    pointer-events: none;
  }

  @supports not (background: light-dark(white, black)) {
    .loader {
      background: #fff;
    }
    @media (prefers-color-scheme: dark) {
      .loader {
        background: #0b0f17;
      }
    }
  }

  .loader-dot {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 3px solid rgba(127, 127, 127, 0.22);
    border-top-color: var(--ksides-brand, #2563eb);
    animation: ksides-spin 0.8s linear infinite;
  }

  .fallback {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 14px;
    padding: 24px;
    background: light-dark(#fff, #0b0f17);
    color: light-dark(#111827, #f8fafc);
  }

  .fallback strong {
    font-size: 17px;
    line-height: 1.35;
  }

  .fallback p {
    margin: 0;
    color: light-dark(#4b5563, #cbd5e1);
    font-size: 14px;
    line-height: 1.6;
  }

  .fallback-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .fallback-actions button {
    min-height: 38px;
    border-radius: 8px;
    border: 1px solid light-dark(#d1d5db, #334155);
    background: light-dark(#f9fafb, #111827);
    color: inherit;
    padding: 0 12px;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .fallback-actions button:first-child {
    border-color: transparent;
    background: var(--ksides-brand, #2563eb);
    color: #fff;
  }

  @keyframes ksides-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .loader-dot {
      animation: none;
    }
  }

  @media (max-width: 520px) {
    .panel,
    :host([position="bottom-left"]) .panel {
      bottom: 0;
      right: 0;
      left: 0;
      width: 100vw;
      height: 100dvh;
      border-radius: 0;
      transform-origin: bottom center;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .trigger,
    .panel {
      transition: none;
    }
  }
`;
const MAX_DRAFT_MESSAGE_LENGTH = 3_500;

export class ChatWidgetElement extends LitElement {
  static override styles: CSSResultGroup = STYLES;

  static override shadowRootOptions: ShadowRootInit = {
    mode: "closed",
    delegatesFocus: false,
  };

  static override properties = {
    appId: { type: String, attribute: "app-id", reflect: true },
    apiBase: { type: String, attribute: "api-base" },
    theme: { type: String, reflect: true },
    position: { type: String, reflect: true },
    locale: { type: String, reflect: true },
    open: { type: Boolean, reflect: true },
    unread: { type: Number, reflect: true },
  };

  appId = "";
  apiBase: string = DEFAULT_APP_BASE;
  theme: WidgetTheme = "auto";
  position: WidgetPosition = "bottom-right";
  locale: WidgetLocale = "ko";
  open = false;
  unread = 0;

  private _iframeLoaded = false;
  private _iframeFailed = false;
  private _iframeUrl = "";
  private _ready = false;
  private _historyPushed = false;
  private _shadow: ShadowRoot | null = null;
  private _loadTimer: ReturnType<typeof setTimeout> | null = null;
  private _onMessage = (e: MessageEvent) => this._handleMessage(e);
  private _onViewport = () => this._applyViewport();
  private _onPopState = (e: PopStateEvent) => this._handlePopState(e);
  private _onKeyDown = (e: KeyboardEvent) => this._handleKeyDown(e);
  private _expectedOrigin = "";
  private _failureCode = "";
  private _returnFocusElement: HTMLElement | null = null;
  private _pendingIdentify: { userId: string; traits?: Record<string, unknown> } | null = null;
  private _pendingDraftMessage: string | null = null;

  override createRenderRoot(): ShadowRoot {
    const root = super.createRenderRoot() as ShadowRoot;
    this._shadow = root;
    return root;
  }

  override connectedCallback(): void {
    try {
      super.connectedCallback();
      this._expectedOrigin = new URL(this.apiBase).origin;
      window.addEventListener("message", this._onMessage);
      window.addEventListener("popstate", this._onPopState);
      window.addEventListener("keydown", this._onKeyDown);
      window.visualViewport?.addEventListener("resize", this._onViewport);
      window.visualViewport?.addEventListener("scroll", this._onViewport);
    } catch (err) {
      this._fail("connect-failed", err);
    }
  }

  override disconnectedCallback(): void {
    try {
      window.removeEventListener("message", this._onMessage);
      window.removeEventListener("popstate", this._onPopState);
      window.removeEventListener("keydown", this._onKeyDown);
      window.visualViewport?.removeEventListener("resize", this._onViewport);
      window.visualViewport?.removeEventListener("scroll", this._onViewport);
      if (this._loadTimer) clearTimeout(this._loadTimer);
    } finally {
      super.disconnectedCallback();
    }
  }

  override willUpdate(changed: PropertyValues): void {
    if (changed.has("apiBase")) {
      try {
        this._expectedOrigin = new URL(this.apiBase).origin;
      } catch {
        this._expectedOrigin = "";
      }
    }
  }

  override render() {
    if (!this.appId) return nothing;
    const triggerLabel = this.locale === "ko" ? (this.open ? "채팅 닫기" : "채팅 열기") : this.open ? "Close chat" : "Open chat";
    const panelLabel = this.locale === "ko" ? "ksides 문의" : "ksides chat";

    return html`
      <button
        class="trigger"
        part="trigger"
        type="button"
        aria-label=${triggerLabel}
        aria-expanded=${this.open ? "true" : "false"}
        aria-haspopup="dialog"
        aria-controls="ksides-chat-panel"
        @click=${this._toggle}
      >
        ${this._renderIcon()}
        ${this.unread > 0
          ? html`<span class="badge" aria-live="polite">${this.unread > 99 ? "99+" : this.unread}</span>`
          : nothing}
      </button>

      ${this._iframeLoaded || this._iframeFailed
        ? html`
            <div
              id="ksides-chat-panel"
              class="panel"
              part="panel"
              data-open=${this.open ? "true" : "false"}
              data-iframe-failed=${this._iframeFailed ? "true" : "false"}
              role="dialog"
              aria-modal="false"
              aria-label=${panelLabel}
              tabindex="-1"
              data-panel-focus-target
            >
              ${this._iframeFailed
                ? this._renderFallback()
                : html`
                    <iframe
                      part="iframe"
                      title=${panelLabel}
                      src=${this._iframeUrl}
                      allow="clipboard-write"
                      referrerpolicy="strict-origin-when-cross-origin"
                      sandbox="allow-scripts allow-same-origin allow-forms"
                    ></iframe>
                    ${!this._ready
                      ? html`<div class="loader" part="loader" aria-hidden="true"><div class="loader-dot"></div></div>`
                      : nothing}
                  `}
            </div>
          `
        : nothing}
    `;
  }

  private _renderFallback() {
    return html`
      <div class="fallback" part="fallback" data-chat-fallback data-failure-code=${this._failureCode || "unknown"}>
        <strong>${this.locale === "ko" ? "문의창을 불러오지 못했습니다" : "Chat could not be loaded"}</strong>
        <p>
          ${this.locale === "ko"
            ? "새로고침 없이 다시 시도할 수 있습니다. 이미 받은 KSC 접수번호가 있다면 나중에 상태를 다시 확인해 주세요."
            : "You can retry without refreshing the page. If you already have a KSC receipt, check its status again later."}
        </p>
        <div class="fallback-actions">
          <button type="button" data-chat-fallback-retry @click=${this._retryLoad}>
            ${this.locale === "ko" ? "다시 시도" : "Retry"}
          </button>
          <button type="button" data-chat-fallback-close @click=${() => this._closePanel()}>
            ${this.locale === "ko" ? "닫기" : "Close"}
          </button>
        </div>
      </div>
    `;
  }

  private _renderIcon() {
    return this.open
      ? html`<svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            fill="none"
          />
        </svg>`
      : html`<svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-9l-4 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z"
            fill="currentColor"
          />
        </svg>`;
  }

  private _toggle = (): void => {
    try {
      if (this.open) this._closePanel();
      else this._openPanel();
    } catch (err) {
      this._fail("toggle-failed", err);
    }
  };

  show(): void {
    try {
      this._openPanel();
    } catch (err) {
      this._fail("show-failed", err);
    }
  }

  draftMessage(text: string): void {
    try {
      const draft = sanitizeDraftMessage(text);
      if (!draft) return;
      this._pendingDraftMessage = draft;
      if (!this._iframeLoaded && !this._iframeFailed) this._loadIframe();
      if (this._ready) this._flushPendingDraftMessage();
    } catch (err) {
      this._fail("draft-message-failed", err);
    }
  }

  private _openPanel(): void {
    if (this.open) return;
    const shadowActive = this._shadow?.activeElement;
    this._returnFocusElement =
      shadowActive instanceof HTMLElement
        ? shadowActive
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    if (!this._iframeLoaded) this._loadIframe();
    this.open = true;
    if (this._isFullScreen() && !this._historyPushed) {
      try {
        history.pushState({ ksidesChat: this.appId || true }, "");
        this._historyPushed = true;
      } catch {
        // ignore; some browsers (e.g. file://) reject pushState
      }
    }
    this._postToIframe({ type: "visibility", visible: true });
    this._dispatch("chat:open", { appId: this.appId });
    void this.updateComplete.then(() => this._focusPanel());
  }

  private _closePanel(fromPopState = false): void {
    if (!this.open) return;
    this.open = false;
    this._postToIframe({ type: "visibility", visible: false });
    this._dispatch("chat:close", { appId: this.appId });
    if (fromPopState) {
      this._historyPushed = false;
    } else {
      this._consumePushedHistory();
    }
    this._restoreFocus();
  }

  private _handleKeyDown(e: KeyboardEvent): void {
    if (!this.open || e.key !== "Escape") return;
    e.preventDefault();
    this._closePanel();
  }

  private _handlePopState(e: PopStateEvent): void {
    void e;
    if (this._historyPushed && this.open) {
      this._closePanel(true);
    }
  }

  private _isFullScreen(): boolean {
    try {
      return window.matchMedia("(max-width: 520px)").matches;
    } catch {
      return false;
    }
  }

  private _loadIframe(): void {
    if (this._iframeLoaded || this._iframeFailed) return;
    this._fetchEmbedToken()
      .then((embedToken) => {
        if (this._iframeFailed || this._iframeLoaded) return;
        this._iframeUrl = buildIframeUrl(this.apiBase, this.appId, this.locale, this.theme, embedToken);
        this._iframeLoaded = true;
        this.requestUpdate();
        void this.updateComplete.then(() => {
          if (this.open) this._focusPanel();
        });
        this._loadTimer = setTimeout(() => {
          if (!this._ready) this._fail("iframe-timeout");
        }, IFRAME_LOAD_TIMEOUT_MS);
      })
      .catch(() => {
        if (this._iframeFailed || this._iframeLoaded) return;
        this._iframeUrl = buildIframeUrl(this.apiBase, this.appId, this.locale, this.theme);
        this._iframeLoaded = true;
        this.requestUpdate();
        void this.updateComplete.then(() => {
          if (this.open) this._focusPanel();
        });
        this._loadTimer = setTimeout(() => {
          if (!this._ready) this._fail("iframe-timeout");
        }, IFRAME_LOAD_TIMEOUT_MS);
      });
  }

  private _retryLoad = (): void => {
    if (this._loadTimer) clearTimeout(this._loadTimer);
    this._iframeFailed = false;
    this._failureCode = "";
    this._iframeLoaded = false;
    this._ready = false;
    this._iframeUrl = "";
    this.requestUpdate();
    this._loadIframe();
    void this.updateComplete.then(() => this._focusPanel());
  };

  private async _fetchEmbedToken(): Promise<string> {
    const base = this.apiBase.replace(/\/+$/, "");
    const res = await fetch(`${base}/widget/embed-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ appId: this.appId, parentOrigin: location.origin }),
    });
    if (!res.ok) {
      return "";
    }
    const data = (await res.json()) as { embedToken?: unknown };
    return typeof data.embedToken === "string" ? data.embedToken : "";
  }

  private _handleMessage(e: MessageEvent): void {
    if (!this._expectedOrigin || e.origin !== this._expectedOrigin) return;
    const data = e.data;
    if (!data || typeof data !== "object" || typeof (data as { type?: unknown }).type !== "string") return;
    const msg = data as InboundMessage;

    try {
      switch (msg.type) {
        case "ready":
          this._ready = true;
          if (this._loadTimer) clearTimeout(this._loadTimer);
          this.requestUpdate();
          this._postToIframe({
            type: "init",
            appId: this.appId,
            theme: this.theme,
            locale: this.locale,
            parentOrigin: location.origin,
            parentUrl: currentSourceUrl(),
          });
          this._postToIframe({ type: "visibility", visible: this.open });
          this._flushPendingIdentify();
          this._flushPendingDraftMessage();
          this._dispatch("chat:ready", { appId: this.appId });
          break;
        case "close":
          this._closePanel();
          break;
        case "unread":
          this.unread = Math.max(0, msg.count | 0);
          this._dispatch("chat:unread", { count: this.unread });
          break;
        case "message":
          if (msg.direction === "out") this._pendingDraftMessage = null;
          this._dispatch("chat:message", { direction: msg.direction, messageId: msg.messageId });
          break;
        case "resolved":
          this._dispatch("chat:resolved", { conversationId: msg.conversationId });
          break;
        case "error":
          this._dispatch("chat:error", { code: msg.code, ...(msg.detail ? { detail: msg.detail } : {}) });
          break;
        case "resize":
          break;
      }
    } catch (err) {
      this._fail("message-handler-failed", err);
    }
  }

  private _postToIframe(msg: OutboundMessage): boolean {
    if (!this._shadow) return false;
    const iframe = this._shadow.querySelector("iframe");
    if (!iframe?.contentWindow || !this._expectedOrigin) return false;
    iframe.contentWindow.postMessage(msg, this._expectedOrigin);
    return true;
  }

  private _applyViewport(): void {
    const vv = window.visualViewport;
    if (!vv || !this._shadow) return;
    const panel = this._shadow.querySelector(".panel") as HTMLElement | null;
    if (!panel) return;

    const keyboardHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    const isFullScreen = window.matchMedia("(max-width: 520px)").matches;

    if (keyboardHeight > 1 && isFullScreen) {
      // Full-screen mobile panel: shrink to the visible viewport so the iframe
      // reflows (header stays at top, composer stays above the keyboard,
      // latest messages stay flush above the composer). Translating the
      // full-height panel up instead would push the conversation area off
      // the top of the screen.
      panel.style.top = `${vv.offsetTop}px`;
      panel.style.bottom = "auto";
      panel.style.height = `${vv.height}px`;
      panel.style.transform = "";
    } else if (keyboardHeight > 1) {
      // Floating panel (desktop / tablet): lift above the keyboard.
      panel.style.transform = `translateY(-${keyboardHeight}px)`;
      panel.style.top = "";
      panel.style.bottom = "";
      panel.style.height = "";
    } else {
      panel.style.top = "";
      panel.style.bottom = "";
      panel.style.height = "";
      panel.style.transform = "";
    }
  }

  private _focusPanel(): void {
    const target =
      this._shadow?.querySelector<HTMLElement>("[data-chat-fallback-retry]") ??
      this._shadow?.querySelector<HTMLElement>("[data-panel-focus-target]");
    target?.focus({ preventScroll: true });
  }

  private _restoreFocus(): void {
    const fallback = this._shadow?.querySelector<HTMLElement>(".trigger");
    const target = this._returnFocusElement && this._returnFocusElement !== this ? this._returnFocusElement : fallback;
    this._returnFocusElement = null;
    target?.focus({ preventScroll: true });
  }

  private _dispatch<T>(name: string, detail: T): void {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  private _consumePushedHistory(): void {
    if (!this._historyPushed) return;
    this._historyPushed = false;
    try {
      history.back();
    } catch {
      // ignore
    }
  }

  private _fail(code: string, detail?: unknown): void {
    this._iframeFailed = true;
    this._iframeLoaded = false;
    this._ready = false;
    this._iframeUrl = "";
    this._failureCode = code;
    if (!this.open) this._consumePushedHistory();
    if (this._loadTimer) clearTimeout(this._loadTimer);
    this.requestUpdate();
    if (this.open) {
      void this.updateComplete.then(() => this._focusPanel());
    }
    const message = detail instanceof Error ? detail.message : typeof detail === "string" ? detail : undefined;
    this._dispatch("chat:error", { code, ...(message ? { detail: message } : {}) });
    if (typeof console !== "undefined" && console.warn) {
      console.warn(`[ksides-chat@${WIDGET_VERSION}] ${code}`, detail ?? "");
    }
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    try {
      const payload = { userId, ...(traits ? { traits } : {}) };
      this._pendingIdentify = payload;
      if (!this._ready) {
        if (!this._iframeLoaded && !this._iframeFailed) this._loadIframe();
        return;
      }
      this._flushPendingIdentify();
    } catch (err) {
      this._fail("identify-failed", err);
    }
  }

  private _flushPendingIdentify(): boolean {
    const pending = this._pendingIdentify;
    if (!pending) return true;
    if (!this._postToIframe({ type: "identify", ...pending })) return false;
    this._pendingIdentify = null;
    return true;
  }

  private _flushPendingDraftMessage(): boolean {
    const pending = this._pendingDraftMessage;
    if (!pending) return true;
    if (!this._postToIframe({ type: "draft", text: pending })) return false;
    this._pendingDraftMessage = null;
    return true;
  }

  reset(): void {
    try {
      this._pendingIdentify = null;
      this._pendingDraftMessage = null;
      this._postToIframe({ type: "reset" });
      this.unread = 0;
      this.open = false;
      this._consumePushedHistory();
    } catch (err) {
      this._fail("reset-failed", err);
    }
  }
}

function sanitizeDraftMessage(value: string): string {
  return String(value ?? "")
    .replace(/\u0000/g, "")
    .replace(/\r\n?/g, "\n")
    .trim()
    .slice(0, MAX_DRAFT_MESSAGE_LENGTH);
}
